/**
 * In-memory holder for the BeeHive access token.
 *
 * The token used to be mirrored into `localStorage.accessToken`. That put a
 * bearer token where every script on the page can read it and where it
 * survives the tab that obtained it, which is what this module removes.
 *
 * It could not simply be deleted. The WebSocket client needs a token
 * synchronously inside `connect()` and on every reconnect, and cannot await
 * BeeHive's async `getValidAccessToken()` from there. A module-scoped variable
 * gives the same synchronous read while keeping the value off disk and out of
 * reach of other scripts: it lives only as long as the page.
 *
 * The store is populated by the module's auth bridge (ContextProvider), which
 * gates rendering until the first token has arrived, so nothing that consumes
 * a token mounts before this is filled.
 *
 * Each federated remote loads its own copy of this module, exactly as it did
 * with the previous `globalGetAccessToken` accessor. That is deliberate: a
 * module's token lifetime follows its own auth bridge.
 */

let accessToken: string | null = null;
let refreshAccessToken: (() => Promise<string | null>) | null = null;

/**
 * Registers BeeHive's async token accessor. Callers that can await should go
 * through {@link getAccessToken} so an expired token is refreshed rather than
 * replayed from the cache.
 */
export const setAccessTokenRefresher = (
  fn: (() => Promise<string | null>) | null,
): void => {
  refreshAccessToken = fn;
};

/** Caches the current token. Pass `null` on logout or session loss. */
export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

/** Drops the cached token and the refresher. */
export const clearAccessToken = (): void => {
  accessToken = null;
};

/**
 * Synchronous read, for callers that genuinely cannot await: the WebSocket
 * connect and reconnect path. Everything else should use
 * {@link getAccessToken}.
 */
export const getAccessTokenSync = (): string | null => accessToken;

/**
 * Preferred accessor. Asks BeeHive for a valid (refreshed) token and falls
 * back to the cached one if the refresher is not registered yet or fails.
 */
export const getAccessToken = async (): Promise<string | null> => {
  if (refreshAccessToken) {
    try {
      const token = await refreshAccessToken();
      if (token) {
        accessToken = token;
        return token;
      }
    } catch (error) {
      console.error("Error refreshing access token:", error);
    }
  }
  return accessToken;
};
